import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Printer, X, Image as ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { PDFDocument } from "pdf-lib";
import { pngToBleedPdfBytes } from "@/lib/printExport";
import { PAPERS, paperById, exportSpec, bleedBoxes, flyerSlug, fitHeadlineSize, overflows, previewScale, pageSizePt, BLEED_IN, SAFE_IN, CSS_DPI, type PaperSpec } from "./flyerArtboard";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useProject } from "@/lib/workflow/useProject";
import { eventSeed } from "@/lib/workflow/eventSeed";

interface Point {
  id: number;
  type: "text" | "image";
  text?: string;
  imageUrl?: string;
}

const COLORS = [
  { hex: "#4F46E5", name: "Indigo" },
  { hex: "#059669", name: "Green" },
  { hex: "#DC2626", name: "Red" },
  { hex: "#D97706", name: "Amber" },
  { hex: "#7C3AED", name: "Violet" },
  { hex: "#0891B2", name: "Cyan" },
];

let idCounter = 200;
const nextId = () => idCounter++;

const TOOL_INFO_CONTENT = {
  what: `Designs a one-page business flyer or brochure with a headline, key points, accent color, and contact information, ready to print or save as a PDF.`,
  how: `Enter your business details, headline, and key points, choose an accent color, and use your browser's print function to save the finished flyer.`,
  tip: `Lead with the single most compelling point first, not last — a flyer typically gets a few seconds of attention, so the most important message needs to be immediately visible, not buried in a bullet list.`,
};

interface ArtboardProps {
  paper: PaperSpec;
  businessName: string;
  headline: string;
  subheadline: string;
  accentColor: string;
  points: Point[];
  contact: string;
  headerImage: string | null;
  bodyRef?: React.Ref<HTMLDivElement>;
  onImageLoad?: () => void;
}

/** The flyer itself, at a fixed CSS size (paper at 96 dpi). Used for the scaled preview and for the offscreen export. */
function FlyerArtboard({ paper, businessName, headline, subheadline, accentColor, points, contact, headerImage, bodyRef, onImageLoad }: ArtboardProps) {
  const { cssW, cssH, k } = exportSpec(paper);
  const pad = Math.round(32 * k);
  const headSize = Math.round(fitHeadlineSize(headline, (cssW - 2 * pad) / k) * k);
  return (
    <div
      data-flyer-artboard
      style={{ width: cssW, height: cssH, display: "flex", flexDirection: "column", background: "#ffffff", color: "#111827", overflow: "hidden", fontSize: 16 * k, lineHeight: 1.5, boxSizing: "border-box" }}
    >
      <div style={{ padding: pad, backgroundColor: accentColor, color: "#fff", position: "relative", overflow: "hidden", flex: "none" }}>
        {headerImage && (
          <>
            <img src={headerImage} alt="" onLoad={onImageLoad} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            {/* A photo's own colors can't be predicted, so a dark overlay keeps white text legible regardless of what's in the photo. */}
            <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.45)" }} />
          </>
        )}
        <div style={{ position: "relative", overflowWrap: "anywhere" }}>
          <p style={{ margin: 0, fontSize: 14 * k, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", opacity: 0.9 }}>{businessName}</p>
          <div style={{ fontSize: headSize, lineHeight: 1.2, fontWeight: 700, marginTop: 8 * k }}>{headline}</div>
          <p style={{ margin: 0, marginTop: 8 * k, opacity: 0.9 }}>{subheadline}</p>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: pad, display: "flex", flexDirection: "column" }}>
        <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 * k }}>
            {points.map(p =>
              p.type === "image" ? (
                <img key={p.id} src={p.imageUrl} alt="" onLoad={onImageLoad} style={{ width: "100%", borderRadius: 8 * k, objectFit: "cover", maxHeight: 192 * k }} />
              ) : (
                <div key={p.id} style={{ display: "flex", alignItems: "flex-start", gap: 12 * k }}>
                  <span style={{ width: 8 * k, height: 8 * k, borderRadius: 9999, marginTop: 8 * k, flex: "none", backgroundColor: accentColor }} />
                  <p style={{ margin: 0, overflowWrap: "anywhere" }}>{p.text}</p>
                </div>
              )
            )}
          </div>
        </div>
        <div style={{ flex: "none", marginTop: 16 * k, paddingTop: 16 * k, borderTop: "1px solid #e5e7eb", fontSize: 14 * k, color: "#6b7280", textAlign: "center", overflowWrap: "anywhere" }}>
          {contact}
        </div>
      </div>
    </div>
  );
}

export default function BrochureFlyerMaker() {
  const { logUsageNow } = useLogUsageOnAction();
  const [businessName, setBusinessName] = useState("Bright Path Consulting");
  const [headline, setHeadline] = useState(
    "Grow Your Business With Confidence"
  );
  const [subheadline, setSubheadline] = useState(
    "Strategy, marketing, and operations support for small businesses"
  );
  const [accentColor, setAccentColor] = useState(COLORS[0].hex);
  const [points, setPoints] = useState<Point[]>([
    { id: nextId(), type: "text", text: "Complimentary strategy session" },
    { id: nextId(), type: "text", text: "Custom growth strategy" },
    { id: nextId(), type: "text", text: "Ongoing support and check-ins" },
  ]);
  const [contact, setContact] = useState(
    "hello@brightpath.com  •  (555) 123-4567  •  brightpath.com"
  );
  const [headerImage, setHeaderImage] = useState<string | null>(null);
  const [paperId, setPaperId] = useState("letter");
  const [showGuides, setShowGuides] = useState(false);
  const [tooLong, setTooLong] = useState(false);
  const [scale, setScale] = useState(1);
  const exportRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const previewBoxRef = useRef<HTMLDivElement>(null);

  // When a project is open (for example after Try a sample), start from its event instead of the example business.
  const [project] = useProject();
  const seed = useMemo(() => eventSeed(project), [project]);
  const seedKey = seed ? JSON.stringify(seed) : "";
  const seededFor = useRef("");
  useEffect(() => {
    if (!seed || seededFor.current === seedKey) return;
    seededFor.current = seedKey;
    const when = [seed.date, seed.time].filter(Boolean).join(" ");
    setBusinessName(seed.title);
    setHeadline(`Join us at ${seed.title}`);
    setSubheadline([when, seed.venue].filter(Boolean).join("  •  "));
    setPoints(seed.highlights.map((t) => ({ id: nextId(), type: "text" as const, text: t })));
    setContact(seed.venue ?? "");
  }, [seed, seedKey]);

  const paper = paperById(paperId);
  const spec = exportSpec(paper);

  // Overflow check on the fixed-size export artboard (identical at any viewport). setState only when the value changes.
  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const inner = el.firstElementChild as HTMLElement | null;
    const over = overflows(inner ? inner.scrollHeight : el.scrollHeight, el.clientHeight);
    setTooLong(prev => (prev === over ? prev : over));
  }, []);
  useLayoutEffect(() => { measure(); }, [measure, businessName, headline, subheadline, points, contact, headerImage, paperId]);

  // Preview scale follows the column width; the value is rounded so a resize can't loop.
  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el) return;
    const update = () => setScale(prev => { const n = previewScale(el.clientWidth, spec.cssW); return prev === n ? prev : n; });
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [spec.cssW]);

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setHeaderImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const addPoint = () =>
    setPoints([...points, { id: nextId(), type: "text", text: "New point" }]);
  const addImagePoint = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPoints(prev => [...prev, { id: nextId(), type: "image", imageUrl: reader.result as string }]);
    };
    reader.readAsDataURL(file);
  };
  const removePoint = (id: number) =>
    setPoints(points.filter(p => p.id !== id));
  const updatePoint = (id: number, text: string) =>
    setPoints(points.map(p => (p.id === id ? { ...p, text } : p)));

  /** Renders the offscreen artboard at exactly paper size x 300 dpi. */
  const renderPng = async (): Promise<Blob> => {
    const el = exportRef.current?.querySelector<HTMLElement>("[data-flyer-artboard]");
    if (!el) throw new Error("artboard missing");
    try { await document.fonts?.ready; } catch { /* ignore */ }
    const url = await toPng(el, { pixelRatio: spec.ratio, width: spec.cssW, height: spec.cssH, cacheBust: true, backgroundColor: "#ffffff" });
    return (await fetch(url)).blob();
  };

  const baseName = flyerSlug(businessName);

  const downloadAsImage = async () => {
    try {
      downloadBlob(await renderPng(), `${baseName}.png`);
      toast.success("Flyer downloaded as image!");
    } catch (err) {
      console.error("Failed to export flyer as image:", err);
      toast.error("Couldn't export the image. Try the Print / Save as PDF option instead.");
    }
  };

  const downloadAsPdf = async () => {
    try {
      const png = new Uint8Array(await (await renderPng()).arrayBuffer());
      const pdf = await PDFDocument.create();
      const img = await pdf.embedPng(png);
      const [pw, ph] = pageSizePt(paper);
      pdf.addPage([pw, ph]).drawImage(img, { x: 0, y: 0, width: pw, height: ph });
      downloadBlob(new Blob([(await pdf.save()) as BlobPart], { type: "application/pdf" }), `${baseName}.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("Couldn't build the PDF. Try the image download instead.");
    }
  };

  const downloadPrintShop = async () => {
    try {
      const bytes = await pngToBleedPdfBytes(await renderPng(), paper.wIn, BLEED_IN);
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), `${baseName}-print-shop.pdf`);
      toast.success("Print-shop PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't build the print-shop PDF");
    }
  };

  const media = bleedBoxes(paper).media;
  const artProps = { paper, businessName, headline, subheadline, accentColor, points, contact, headerImage };
  const guideInset = SAFE_IN * CSS_DPI;

  return (
    <ToolContainer>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #flyer-print-area, #flyer-print-area * { visibility: visible; }
          #flyer-print-area {
            position: absolute; top: 0; left: 0;
            width: ${spec.cssW}px !important; height: ${spec.cssH}px !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          #flyer-print-area .flyer-scale { transform: none !important; }
          @page { size: ${paper.wIn.toFixed(3)}in ${paper.hIn.toFixed(3)}in; margin: 0; }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4 print:hidden min-w-0">
          <div>
            <Label htmlFor="flyer-paper" className="mb-1 block text-sm">Paper size</Label>
            <select
              id="flyer-paper"
              value={paperId}
              onChange={e => setPaperId(e.target.value)}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              {PAPERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="flyer-business" className="mb-1 block text-sm">Business Name</Label>
            <Input
              id="flyer-business"
              value={businessName}
              onChange={e => setBusinessName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="flyer-headline" className="mb-1 block text-sm">Headline</Label>
            <Input
              id="flyer-headline"
              value={headline}
              onChange={e => setHeadline(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="flyer-sub" className="mb-1 block text-sm">Subheadline</Label>
            <Textarea
              id="flyer-sub"
              value={subheadline}
              onChange={e => setSubheadline(e.target.value)}
              className="min-h-[60px]"
            />
          </div>

          <div>
            <Label className="mb-1 block text-sm">Accent Color</Label>
            <div className="flex gap-2" role="group" aria-label="Accent colour">
              {COLORS.map(c => (
                <button
                  key={c.hex}
                  type="button"
                  aria-label={`Colour: ${c.name}`}
                  aria-pressed={accentColor === c.hex}
                  title={c.name}
                  onClick={() => setAccentColor(c.hex)}
                  className={`w-8 h-8 rounded-full border-2 ${accentColor === c.hex ? "border-foreground" : "border-transparent"}`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-1 block text-sm">Header Background Photo (optional)</Label>
            {headerImage ? (
              <div className="flex items-center gap-2">
                <img src={headerImage} alt="Header photo preview" className="h-12 w-20 object-cover rounded border" />
                <Button size="sm" variant="ghost" onClick={() => setHeaderImage(null)}>
                  <X className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleImageUpload} label="Drop a photo for the header background" />
            )}
          </div>

          <div>
            <Label className="mb-1 block text-sm">Key Points</Label>
            <div className="space-y-2">
              {points.map((p, i) => (
                <div key={p.id} className="flex gap-2">
                  {p.type === "text" ? (
                    <Input
                      aria-label={`Point ${i + 1}`}
                      value={p.text}
                      onChange={e => updatePoint(p.id, e.target.value)}
                    />
                  ) : (
                    <div className="flex-1 flex items-center gap-2 border rounded-md px-2 py-1">
                      <img src={p.imageUrl} alt={`Image block ${i + 1}`} className="h-8 w-12 object-cover rounded" />
                      <span className="text-xs text-muted-foreground">Image block</span>
                    </div>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove point ${i + 1}`}
                    onClick={() => removePoint(p.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={addPoint}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Point
              </Button>
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  aria-label="Add image block"
                  onChange={e => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    addImagePoint(files);
                    e.target.value = "";
                  }}
                />
                <Button size="sm" variant="outline" asChild>
                  <span>
                    <ImageIcon className="h-3.5 w-3.5 mr-1" /> Add Image
                  </span>
                </Button>
              </label>
            </div>
          </div>

          <div>
            <Label htmlFor="flyer-contact" className="mb-1 block text-sm">Contact Info</Label>
            <Input id="flyer-contact" value={contact} onChange={e => setContact(e.target.value)} />
          </div>

          {tooLong && (
            <p role="alert" className="text-xs text-destructive">
              The content is taller than the page, so the lowest points will be cut off in the print and downloads. Shorten the text, remove a point or image, or pick a larger paper size.
            </p>
          )}

          <Button className="w-full" onClick={async () => { if (await logUsageNow("download")) window.print(); }}>
            <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
          </Button>
          <Button className="w-full" variant="outline" onClick={downloadAsImage}>
            <Download className="h-4 w-4 mr-2" /> Download as Image
          </Button>
          <Button className="w-full" variant="outline" onClick={downloadAsPdf}>Download as PDF ({paper.id === "letter" ? "Letter" : paper.id === "half-letter" ? "half letter" : paper.id.toUpperCase()})</Button>
          <div className="rounded-md border p-3 space-y-2">
            <Button className="w-full" variant="outline" onClick={downloadPrintShop}>Download print-shop PDF (0.125" bleed)</Button>
            <p className="text-xs text-muted-foreground">
              Page = {(media.w / 72).toFixed(2)} x {(media.h / 72).toFixed(2)} in: the paper size plus 0.125" on every side, with TrimBox set. The design's edge is extended into the bleed so there's no white sliver after cutting; keep text at least 0.25" inside the edge.
            </p>
          </div>
        </div>

        <div className="min-w-0">
          <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground print:hidden">
            <input type="checkbox" checked={showGuides} onChange={e => setShowGuides(e.target.checked)} />
            Show trim and safe-zone guides
          </label>
          <div ref={previewBoxRef} className="w-full">
            <div
              id="flyer-print-area"
              className="rounded-lg border overflow-hidden relative"
              style={{ width: spec.cssW * scale, height: spec.cssH * scale, maxWidth: "100%" }}
            >
              <div className="flyer-scale" style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: spec.cssW, height: spec.cssH }}>
                <FlyerArtboard {...artProps} />
              </div>
              {showGuides && (
                <div className="absolute inset-0 pointer-events-none print:hidden" aria-hidden="true">
                  <div className="absolute inset-0 border border-dashed border-red-500" />
                  <div
                    className="absolute border border-dashed border-sky-500"
                    style={{ inset: guideInset * scale }}
                  />
                </div>
              )}
            </div>
          </div>
          {showGuides && (
            <p className="mt-2 text-xs text-muted-foreground print:hidden">Red: trim (cut) edge. Blue: safe zone, 0.25" inside. The 0.125" bleed is added outside the trim in the print-shop PDF.</p>
          )}
        </div>
      </div>

      {/* Fixed-size offscreen artboard: the downloads are drawn from this, never from the live preview. */}
      <div ref={exportRef} aria-hidden="true" style={{ position: "fixed", left: -100000, top: 0, pointerEvents: "none" }}>
        <FlyerArtboard {...artProps} bodyRef={bodyRef} onImageLoad={measure} />
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
